<template>
  <div class="flex flex-col flex-1 min-h-0 relative">
    <!-- Egg Canvas Area -->
    <div
      ref="eggCanvasArea"
      class="flex-1 flex items-center justify-center px-4 min-h-0 overflow-hidden"
    >
      <div class="relative shrink-0" :style="{ width: `${eggWidth}px`, height: `${eggHeight}px` }">
        <!-- Ground shadow beneath egg -->
        <div
          class="absolute left-1/2 -translate-x-1/2 rounded-[50%]"
          :style="{
            bottom: `${eggHeight * 0.06}px`,
            width: `${eggWidth * 0.55}px`,
            height: `${eggHeight * 0.05}px`,
            background:
              'radial-gradient(ellipse, rgba(0,0,0,0.22) 0%, rgba(0,0,0,0.08) 40%, transparent 70%)',
            filter: 'blur(4px)',
          }"
        />
        <div
          ref="eggContainer"
          class="relative"
          :style="{ width: `${eggWidth}px`, height: `${eggHeight}px` }"
        >
          <!-- Layer 1: Egg Template (background) -->
          <EggTemplate
            ref="eggTemplate"
            :template-id="selectedTemplate"
            :custom-color="customBgColor"
            :width="eggWidth"
            :height="eggHeight"
            class="absolute inset-0"
            style="z-index: 0"
          />

          <!-- Layer 2: Photo (between template and drawing) -->
          <EggPhotoLayer
            v-if="photoImage"
            ref="eggPhoto"
            :photo-image="photoImage"
            :transform="photoTransform"
            :width="eggWidth"
            :height="eggHeight"
            :interactive="interactionMode === 'position'"
            @update:transform="photoTransform = $event"
          />

          <!-- Layer 3: Drawing Canvas -->
          <EggCanvas
            ref="eggCanvas"
            :is-active="interactionMode === 'draw'"
            :brush-color="brushColor"
            :brush-size="brushSize"
            :container-width="eggWidth"
            :container-height="eggHeight"
            @content-changed="onDrawingChanged"
          />
        </div>
      </div>
    </div>

    <!-- Hidden file input for photo picker -->
    <input ref="photoInput" type="file" accept="image/*" class="hidden" @change="onPhotoSelected" />

    <!-- Background Colour Picker -->
    <div class="px-4 py-1 shrink-0">
      <div class="flex gap-2 justify-center items-center">
        <button
          v-for="tmpl in eggTemplates"
          :key="tmpl.id"
          class="w-9 h-11 rounded-full transition-all"
          :class="selectedTemplate === tmpl.id && !customBgColor ? 'scale-110 shadow-md' : ''"
          :style="{
            background: `linear-gradient(135deg, ${tmpl.baseColor}, ${tmpl.accentColor})`,
            border:
              selectedTemplate === tmpl.id && !customBgColor
                ? '2.5px solid #42362B'
                : '2.5px solid #D3D8DE',
          }"
          :title="tmpl.name"
          @click="selectTemplate(tmpl.id)"
        ></button>
        <label
          class="w-9 h-11 rounded-full cursor-pointer flex items-center justify-center overflow-hidden transition-all"
          :class="customBgColor ? 'scale-110 shadow-md' : ''"
          :style="{
            background: 'conic-gradient(red, yellow, lime, aqua, blue, magenta, red)',
            border: customBgColor ? '2.5px solid #42362B' : '2.5px solid #D3D8DE',
          }"
        >
          <input
            type="color"
            :value="customBgColor || '#FFE0EC'"
            class="opacity-0 absolute w-0 h-0"
            @input="onCustomBgColor($event.target.value)"
          />
        </label>
      </div>
    </div>

    <!-- Toolbar -->
    <DrawingToolbar
      :selected-color="brushColor"
      :brush-size="brushSize"
      :has-photo="!!photoImage"
      :mode="interactionMode"
      @color-change="brushColor = $event"
      @brush-size-change="brushSize = $event"
      @undo="handleUndo"
      @clear="handleClear"
      @add-photo="triggerPhotoPicker"
      @toggle-mode="toggleMode"
      @remove-photo="removePhoto"
    >
      <template #actions>
        <slot name="actions" />
      </template>
    </DrawingToolbar>
  </div>
</template>

<script>
import EggTemplate, { EGG_TEMPLATES } from './EggTemplate.vue';
import EggCanvas from './EggCanvas.vue';
import EggPhotoLayer from './EggPhotoLayer.vue';
import DrawingToolbar from './DrawingToolbar.vue';

export default {
  name: 'EasterEggDecorate',
  components: {
    EggTemplate,
    EggCanvas,
    EggPhotoLayer,
    DrawingToolbar,
  },
  emits: ['has-content', 'mode-change'],
  data() {
    return {
      brushColor: '#FF4444',
      brushSize: 5,
      hasDrawingContent: false,
      eggWidth: 300,
      eggHeight: 375,
      selectedTemplate: 'white',
      customBgColor: null,
      eggTemplates: EGG_TEMPLATES,
      photoImage: null,
      photoTransform: { x: 0, y: 0, scale: 1, rotation: 0 },
      interactionMode: 'draw',
    };
  },
  computed: {
    hasContent() {
      return (
        this.hasDrawingContent ||
        this.selectedTemplate !== 'white' ||
        !!this.photoImage ||
        !!this.customBgColor
      );
    },
  },
  watch: {
    hasContent(val) {
      this.$emit('has-content', val);
    },
  },
  mounted() {
    // Wait for layout to settle before measuring container height
    this.$nextTick(() => {
      requestAnimationFrame(() => this.calculateEggSize());
    });
    window.addEventListener('resize', this.calculateEggSize);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.calculateEggSize);
  },
  methods: {
    calculateEggSize() {
      const widthBased = Math.min(window.innerWidth * 0.85, 400);
      // Measure the egg canvas container's actual available height
      const canvasArea = this.$refs.eggCanvasArea;
      const availableHeight = canvasArea ? canvasArea.clientHeight - 16 : window.innerHeight - 420;
      const heightBased = Math.floor(availableHeight / 1.25);
      const maxWidth = Math.min(widthBased, heightBased);
      this.eggWidth = Math.max(maxWidth, 150);
      this.eggHeight = Math.round(this.eggWidth * 1.25);
    },

    selectTemplate(id) {
      this.selectedTemplate = id;
      this.customBgColor = null;
    },

    onCustomBgColor(color) {
      this.customBgColor = color;
    },

    onDrawingChanged(hasContent) {
      this.hasDrawingContent = hasContent;
    },

    handleUndo() {
      this.$refs.eggCanvas?.undo();
    },

    handleClear() {
      this.$refs.eggCanvas?.clear();
    },

    triggerPhotoPicker() {
      this.$refs.photoInput?.click();
    },

    onPhotoSelected(event) {
      const file = event.target.files?.[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = e => {
        this.photoImage = e.target.result;
        this.photoTransform = { x: 0, y: 0, scale: 1, rotation: 0 };
        this.interactionMode = 'position';
      };
      reader.readAsDataURL(file);

      // Reset input so the same file can be selected again
      event.target.value = '';
    },

    toggleMode(mode) {
      this.interactionMode = mode || (this.interactionMode === 'draw' ? 'position' : 'draw');
      this.$emit('mode-change', this.interactionMode);
    },

    removePhoto() {
      this.photoImage = null;
      this.photoTransform = { x: 0, y: 0, scale: 1, rotation: 0 };
      this.interactionMode = 'draw';
      this.$emit('mode-change', 'draw');
    },

    undo() {
      this.handleUndo();
    },

    clear() {
      this.handleClear();
    },

    getTemplateCanvas() {
      return this.$refs.eggTemplate?.getCanvas();
    },

    getDrawingCanvas() {
      return this.$refs.eggCanvas?.getCanvas();
    },

    getPhotoCanvas() {
      return this.$refs.eggPhoto?.getCanvas() || null;
    },

    getPhotoData() {
      return this.photoImage;
    },

    getPhotoTransform() {
      return { ...this.photoTransform };
    },

    getDisplayWidth() {
      return this.eggWidth;
    },
  },
};
</script>
